/**
 * BIL Store Locator controller.
 * Handles state, controls, geo-search, map/list synchronization, and analytics.
 */
(function (window, document) {
  'use strict';

  function init(rootId) {
    var root = document.getElementById(rootId);
    if (!root || root.dataset.bilLocatorReady === 'true') return;
    root.dataset.bilLocatorReady = 'true';

    var configNode = root.querySelector('[data-bil-config]');
    var config = JSON.parse(configNode.textContent || '{}');
    var elements = window.BILStoreLocatorUI.getElements(root);

    var state = {
      root: root,
      config: config,
      elements: elements,
      allStores: [],
      filters: { state: '', region: '', retailer: '' },
      searchTerm: '',
      searchMode: 'text', // text | geo
      origin: null,
      mapContext: null,
      infoWindow: null,
      markerEntries: [],
      latestSearchId: 0
    };

    wireControls(state);

    window.BILStoreLocatorData.loadStores(config.locationsUrl)
      .then(function (stores) {
        state.allStores = stores;
        window.BILStoreLocatorUI.populateFilters(
          elements,
          window.BILStoreLocatorData.buildFilterOptions(stores)
        );
        render(state);
        track(state, 'view', { result_count: stores.length });

        if (config.googleMapsApiKey) {
          return window.BILStoreLocatorMap.loadGoogleMaps(config.googleMapsApiKey)
            .then(function () {
              return window.BILStoreLocatorMap.createMap(elements.map, config);
            })
            .then(function (mapContext) {
              state.mapContext = mapContext;
              state.infoWindow = new mapContext.InfoWindow();
              render(state);
            })
            .catch(function (error) {
              window.BILStoreLocatorUI.setMapStatus(
                elements,
                'Map unavailable. You can still search the store list.'
              );
              console.warn('[BIL Locator]', error);
            });
        }

        window.BILStoreLocatorUI.setMapStatus(
          elements,
          'Add a Google Maps API key in the section settings to display the map.'
        );
        return null;
      })
      .catch(function (error) {
        elements.list.innerHTML = '';
        elements.empty.hidden = false;
        window.BILStoreLocatorUI.setMapStatus(elements, 'Store locations could not be loaded.');
        console.error('[BIL Locator]', error);
      });
  }

  function facetStores(state) {
    return window.BILStoreLocatorData.filterByFacets(state.allStores, state.filters);
  }

  function resultSet(state) {
    var base = facetStores(state);
    if (state.searchTerm && state.searchMode === 'text') {
      base = window.BILStoreLocatorData.filterByText(base, state.searchTerm);
    }
    if (state.origin) {
      base = window.BILStoreLocatorData.sortByDistance(base, state.origin);
    }
    return base;
  }

  function render(state) {
    if (!state.allStores.length) return;

    var results = resultSet(state);
    var shown = results;
    var coordinateCount = results.filter(window.BILStoreLocatorData.hasCoordinates).length;
    var note = '';

    if (state.origin && state.searchMode === 'geo') {
      note = 'nearest to your search';
    } else if (state.origin) {
      note = 'sorted by distance';
    }

    window.BILStoreLocatorUI.renderResults(state.elements, shown, {
      totalCount: results.length,
      note: note,
      summary: results.length === 0 ? 'No stores found' : ''
    });

    if (state.mapContext) {
      window.BILStoreLocatorMap.clearMarkers(state.markerEntries);
      state.markerEntries = window.BILStoreLocatorMap.renderMarkers(
        state.mapContext,
        results,
        {
          accentColor: state.config.accentColor,
          onMarkerClick: function (store, marker) {
            window.BILStoreLocatorMap.openInfoWindow(
              state.mapContext, marker, store, state.infoWindow
            );
            window.BILStoreLocatorUI.setActiveCard(state.elements, store.id);
            track(state, 'pin_click', storePayload(store, state));
          }
        }
      );

      if (!coordinateCount) {
        window.BILStoreLocatorUI.setMapStatus(
          state.elements,
          'Store locations are still being verified. The store list and directions are available.'
        );
      } else {
        window.BILStoreLocatorUI.setMapStatus(state.elements, '');
        if (state.origin && state.searchMode === 'geo') {
          state.mapContext.map.setCenter({ lat: state.origin.lat, lng: state.origin.lng });
          state.mapContext.map.setZoom(9);
        } else if (!state.searchTerm && !state.origin) {
          window.BILStoreLocatorMap.fitToMarkers(state.mapContext, state.markerEntries, {
            lat: Number(state.config.defaultLat),
            lng: Number(state.config.defaultLng)
          });
        } else {
          window.BILStoreLocatorMap.fitToMarkers(state.mapContext, state.markerEntries);
        }
      }
    }

    if (state.searchTerm && results.length === 0) {
      track(state, 'no_results', {
        search_term: state.searchTerm,
        search_type: window.BILStoreLocatorData.detectSearchType(state.searchTerm),
        result_count: 0
      });
    }
  }

  function handleSearch(state) {
    var term = state.elements.search.value.trim();
    var searchType = window.BILStoreLocatorData.detectSearchType(term);
    var requestId = ++state.latestSearchId;

    state.searchTerm = term;
    state.searchMode = 'text';
    state.origin = null;
    render(state);

    track(state, 'search', {
      search_term: term,
      search_type: searchType,
      result_count: resultSet(state).length
    });

    // ZIPs always represent an origin, even when no stores share the ZIP.
    // Cities with no direct local match also become "nearest store" searches.
    if (!term || searchType === 'state' || !state.mapContext) return;

    window.BILStoreLocatorMap.geocodeUS(term)
      .then(function (origin) {
        if (requestId !== state.latestSearchId) return;

        var directMatches = window.BILStoreLocatorData.filterByText(facetStores(state), term);
        state.origin = origin;

        // This fixes Claude's original bug: a ZIP with no exact store still returns
        // nearby stores instead of an empty list.
        if (searchType === 'zip' || directMatches.length === 0) {
          state.searchMode = 'geo';
        }
        render(state);
      })
      .catch(function () {
        // Text filtering remains useful even if Google cannot geocode a term.
      });
  }

  function handleUseLocation(state) {
    if (!state.config.enableGeolocation || !navigator.geolocation) {
      window.BILStoreLocatorUI.setMapStatus(
        state.elements,
        'Location services are unavailable. Search by ZIP or city instead.'
      );
      return;
    }

    track(state, 'geolocate', { origin_type: 'browser_location' });
    navigator.geolocation.getCurrentPosition(
      function (position) {
        state.origin = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          label: 'your location'
        };
        state.searchTerm = '';
        state.searchMode = 'geo';
        state.elements.search.value = '';
        render(state);
      },
      function () {
        window.BILStoreLocatorUI.setMapStatus(
          state.elements,
          'We could not access your location. Allow location access or search by ZIP.'
        );
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  function handleReset(state) {
    state.filters = { state: '', region: '', retailer: '' };
    state.searchTerm = '';
    state.searchMode = 'text';
    state.origin = null;
    state.latestSearchId += 1;
    state.elements.search.value = '';
    state.elements.state.value = '';
    state.elements.region.value = '';
    state.elements.retailer.value = '';
    render(state);
    track(state, 'reset', {});
  }

  function findStore(state, id) {
    return state.allStores.find(function (store) { return store.id === id; });
  }

  function storePayload(store, state) {
    return {
      store_id: store.id,
      store_name: store.store_name,
      retailer: store.retailer,
      city: store.city,
      state: store.state,
      zip: store.zip,
      selected_state: state.filters.state || null,
      selected_region: state.filters.region || null,
      selected_retailer: state.filters.retailer || null
    };
  }

  function track(state, action, payload) {
    if (!state.config.enableAnalytics ||
      typeof window.trackStoreLocatorEvent !== 'function') return;
    window.trackStoreLocatorEvent(action, Object.assign({
      selected_state: state.filters.state || null,
      selected_region: state.filters.region || null,
      selected_retailer: state.filters.retailer || null
    }, payload || {}));
  }

  function wireControls(state) {
    var elements = state.elements;

    elements.form.addEventListener('submit', function (event) {
      event.preventDefault();
      handleSearch(state);
    });

    elements.state.addEventListener('change', function () {
      state.filters.state = elements.state.value;
      render(state);
      track(state, 'filter', { filter_type: 'state', selected_state: state.filters.state });
    });

    elements.region.addEventListener('change', function () {
      state.filters.region = elements.region.value;
      render(state);
      track(state, 'filter', { filter_type: 'market', selected_region: state.filters.region });
    });

    elements.retailer.addEventListener('change', function () {
      state.filters.retailer = elements.retailer.value;
      render(state);
      track(state, 'filter', { filter_type: 'retailer', selected_retailer: state.filters.retailer });
    });

    if (elements.useLocation) {
      elements.useLocation.addEventListener('click', function () {
        handleUseLocation(state);
      });
    }

    elements.reset.addEventListener('click', function () {
      handleReset(state);
    });

    elements.list.addEventListener('click', function (event) {
      var actionElement = event.target.closest('[data-action]');
      var card = event.target.closest('[data-bil-store-card]');
      if (!card) return;
      var store = findStore(state, card.getAttribute('data-store-id'));
      if (!store) return;

      if (actionElement) {
        var action = actionElement.getAttribute('data-action');
        if (action === 'view-map') {
          var entry = state.markerEntries.find(function (item) {
            return item.store.id === store.id;
          });
          if (entry && state.mapContext) {
            window.BILStoreLocatorMap.focusStore(
              state.mapContext, entry.marker, store, state.infoWindow
            );
          }
          window.BILStoreLocatorUI.setActiveCard(state.elements, store.id);
          track(state, 'view_map', storePayload(store, state));
        } else if (action === 'directions') {
          track(state, 'directions', storePayload(store, state));
        }
      } else {
        window.BILStoreLocatorUI.setActiveCard(state.elements, store.id);
        track(state, 'store_click', storePayload(store, state));
      }
    });

    elements.list.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      var card = event.target.closest('[data-bil-store-card]');
      if (!card) return;
      event.preventDefault();
      card.click();
    });
  }

  window.BILStoreLocator = { init: init };
})(window, document);
